import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, RefreshCw, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const METRIC_FIELDS = [
  { key: 'waist_cm',  label: 'Талия',            unit: 'см', placeholder: '80.0', min: 40,  max: 200 },
  { key: 'chest_cm',  label: 'Гръдна обиколка',  unit: 'см', placeholder: '90.0', min: 50,  max: 180 },
  { key: 'bicep_cm',  label: 'Ръка / Бицепс',    unit: 'см', placeholder: '32.0', min: 15,  max: 80  },
  { key: 'thigh_cm',  label: 'Крак / Бедро',     unit: 'см', placeholder: '55.0', min: 30,  max: 100 },
];

export const CheckinModal = ({ onClose, onSuccess, initialWeight }) => {
  const tdeeData = JSON.parse(localStorage.getItem('portion_tdee') || 'null');
  // Metrics chosen in the quiz — fallback to ['waist_cm'] for legacy users
  const trackedMetrics = tdeeData?.trackedMetrics ?? ['waist_cm'];

  const [weight, setWeight] = useState('');
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [savedWeight, setSavedWeight] = useState(null);
  const [savedMetrics, setSavedMetrics] = useState({});
  const [newCalories, setNewCalories] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  const updateMetric = (key, val) => setMetrics((m) => ({ ...m, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const weightVal = parseFloat(weight);
    if (!weight || isNaN(weightVal) || weightVal <= 0) {
      toast.error('Въведи валидно тегло');
      return;
    }
    setLoading(true);
    try {
      const payload = { weight: weightVal };
      METRIC_FIELDS.forEach(({ key }) => {
        payload[key] = metrics[key] ? parseFloat(metrics[key]) : null;
      });
      await axios.post(`${API}/progress/checkin`, payload);
      setSavedWeight(weightVal);
      setSavedMetrics({ ...metrics });
      setSubmitted(true);
      onSuccess?.();
    } catch {
      toast.error('Грешка при запазване на прогреса');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const activity = tdeeData?.activity || 'moderate';
      const res = await axios.post(`${API}/profile/recalculate-calories`, { activity });
      const tdee = res.data.tdee;
      setNewCalories(tdee);
      if (tdeeData) {
        localStorage.setItem('portion_tdee', JSON.stringify({
          ...tdeeData, target_calories: tdee, weight: savedWeight,
        }));
      }
      toast.success(`Калориите са актуализирани: ${tdee} ккал`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Грешка при преизчисляване');
    } finally {
      setRecalculating(false);
    }
  };

  const weightDiffers =
    submitted && savedWeight !== null && initialWeight != null &&
    Math.abs(savedWeight - initialWeight) >= 0.5;

  const savedMetricsSummary = METRIC_FIELDS
    .filter((f) => savedMetrics[f.key] && parseFloat(savedMetrics[f.key]) > 0)
    .map((f) => `${f.label}: ${parseFloat(savedMetrics[f.key])} ${f.unit}`);

  // Fields to display: only those matching trackedMetrics from quiz
  const activeFields = METRIC_FIELDS.filter((f) => trackedMetrics.includes(f.key));

  return (
    <AnimatePresence>
      <motion.div
        key="checkin-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          key="checkin-card"
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 26, stiffness: 320 }}
          className="relative bg-white dark:bg-slate-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-100 dark:border-slate-700 max-h-[90vh] overflow-y-auto"
        >
          <button
            onClick={onClose}
            data-testid="checkin-modal-close"
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>

          {!submitted ? (
            <>
              <div className="mb-6">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2
                  data-testid="checkin-modal-title"
                  className="text-xl font-bold text-gray-900 dark:text-gray-100"
                >
                  Време е за седмичния ти отчет! 🚀
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                  Запиши прогреса си и следи промяната седмица по седмица
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Weight — always required */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Тегло (задължително)
                    <span className="text-gray-400 dark:text-gray-500 font-normal ml-1.5">кг</span>
                  </label>
                  <input
                    data-testid="checkin-weight-input"
                    type="number" step="0.1" min="20" max="300"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="70.5"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base transition-colors"
                  />
                </div>

                {/* Dynamic fields from onboarding metric selection */}
                {activeFields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      {field.label}
                      <span className="text-gray-400 dark:text-gray-500 font-normal ml-1"> (опционално)</span>
                      <span className="text-gray-400 dark:text-gray-500 font-normal ml-1.5">{field.unit}</span>
                    </label>
                    <input
                      data-testid={`checkin-${field.key}-input`}
                      type="number" step="0.5"
                      min={field.min} max={field.max}
                      value={metrics[field.key] || ''}
                      onChange={(e) => updateMetric(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base transition-colors"
                    />
                  </div>
                ))}

                <button
                  type="submit"
                  data-testid="checkin-submit-btn"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white font-semibold text-base transition-colors flex items-center justify-center gap-2 mt-2"
                >
                  {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
                  Запази прогреса
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="checkin-success-state">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2
                data-testid="checkin-success-title"
                className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2"
              >
                Прогресът е записан!
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
                Тегло:{' '}
                <strong className="text-gray-900 dark:text-gray-100">{savedWeight} кг</strong>
              </p>
              {savedMetricsSummary.length > 0 && (
                <p className="text-gray-400 dark:text-gray-500 text-xs mb-5 leading-relaxed">
                  {savedMetricsSummary.join(' · ')}
                </p>
              )}

              {weightDiffers && !newCalories && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-2xl p-4 mb-4 text-left">
                  <p className="text-sm text-amber-800 dark:text-amber-300 mb-3">
                    Теглото ти се е променило спрямо началото. Искаш ли да преизчислим дневните ти калории?
                  </p>
                  <button
                    data-testid="recalculate-calories-btn"
                    onClick={handleRecalculate}
                    disabled={recalculating}
                    className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                  >
                    {recalculating
                      ? <RefreshCw className="w-4 h-4 animate-spin" />
                      : <RefreshCw className="w-4 h-4" />}
                    Преизчисли дневните ми калории
                  </button>
                </div>
              )}

              {newCalories && (
                <div
                  data-testid="new-calories-result"
                  className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl p-4 mb-4"
                >
                  <p className="text-sm text-emerald-800 dark:text-emerald-300">
                    Новата ти дневна цел:{' '}
                    <strong className="text-emerald-700 dark:text-emerald-400 text-base">
                      {newCalories} ккал
                    </strong>
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">
                    Следващият план ще бъде генериран с новите калории
                  </p>
                </div>
              )}

              <button
                data-testid="checkin-close-btn"
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
              >
                Затвори
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
